<template>
  <div :class="['file-gallery', itemSizeClass]">
    <FileGalleryItem
      class="clickable"
      v-for="(file, index) in files"
      :thumbnail-url="file.thumbnailUrl"
      :key="file.id"
      :is-removable="isRemovable"
      @click="fileViewerModalRef?.show(index)"
      @remove="$emit('remove', file)"
    />

    <FileGalleryItem v-for="uploadFile in uploadFiles" :is-loading="true" :key="uploadFile.id" />

    <slot />

    <FileViewerModal :files="files" ref="fileViewerModalRef">
      <template #actions="{ activeFile }">
        <IconButton
          v-if="isRemovable"
          variant="secondary"
          size="medium"
          shape="square"
          icon="trash"
          @click="$emit('remove', activeFile)"
        />
      </template>
    </FileViewerModal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { IconButton } from '@tallo/design-system';

import { FileDto } from '../interfaces/file.dto';
import { UploadFile } from '../interfaces/upload-file.interface';
import FileGalleryItem from './file-gallery-item.vue';
import FileViewerModal from './file-viewer-modal.vue';

const props = withDefaults(
  defineProps<{
    files?: FileDto[];
    uploadFiles?: UploadFile[];
    isRemovable?: boolean;
    size?: 'xs' | 'sm';
  }>(),
  {
    size: 'xs',
  },
);

defineEmits<{ remove: [file: FileDto] }>();

const fileViewerModalRef = ref<InstanceType<typeof FileViewerModal>>();
const files = computed(() => props.files || []);
const uploadFiles = computed(() => props.uploadFiles || []);
const itemSizeClass = computed(() => `size-${props.size}`);
</script>

<style scoped lang="scss">
.file-gallery {
  --size: 3.5rem;

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(var(--size), 1fr));
  grid-auto-rows: 1fr;
  gap: var(--t-spacing-sm);

  &.size-sm {
    --size: 6rem;
  }

  & > * {
    width: 100%;
    height: 100%;
    aspect-ratio: 1;
  }
}

.clickable:hover {
  cursor: pointer;
}
</style>
